import "./index.scss";

import { Box, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { ReactComponent as IconCardio } from "assets/images/included-programs/cardiovascular-print.svg";
import { ReactComponent as IconFlexibility } from "assets/images/included-programs/flexibility-print.svg";
import { ReactComponent as IconStrength } from "assets/images/included-programs/strength-print.svg";
import { ReactComponent as IconLogo } from "assets/images/logo/logo.svg";
import { DATE_FORMAT } from "constants/index";
import html2pdf from "html2pdf.js";
import { CardioProgram, StrengthProgram, WorkoutPlan } from "models/workoutplan";
import moment from "moment";
import {
  FC,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useReactToPrint } from "react-to-print";

export interface GeneratePdfRef {
  printContent: any;
  exportContent: any;
}

const GeneratePdfComp: FC<{
  title: string;
  type: string;
  ref?: any;
  className?: string;
  size?: "a4" | "a3" | "a2";
  workoutPlan: WorkoutPlan;
  cardioProgram?: CardioProgram;
  strengthProgram?: StrengthProgram;
  perceivedExertion: string;
  workoutPlanGoals: string;
  flexibilityUpperBodyStretchesOptionsFromParent?: any[];
  flexibilityLowerBodyStretchesOptionsFromParent?: any[];
}> = forwardRef((props, ref) => {
  const {
    title,
    type,
    className,
    size = "a4",
    workoutPlan,
    cardioProgram,
    strengthProgram,
    perceivedExertion,
    workoutPlanGoals,
    flexibilityUpperBodyStretchesOptionsFromParent,
    flexibilityLowerBodyStretchesOptionsFromParent,
  } = props;

  const pdfPrintRef = useRef(null);

  useImperativeHandle(
    ref,
    (): GeneratePdfRef => ({
      exportContent: handleExportPDF,
      printContent: handlePrintPDF,
    })
  );

  //#region event handlers
  const handlePrintPDF = useReactToPrint({
    content: () => pdfPrintRef.current,
    removeAfterPrint: true,
  });

  const handleExportPDF = useCallback(() => {
    const printElement = document.querySelector(`#${type}-pdf-print`) as HTMLElement;
    var opt = {
      filename: `[${moment().format(DATE_FORMAT)}]-${type}-report.pdf`,
      html2canvas: { scale: 1 },
      image: { quality: 0.98, type: "jpeg" },
      jsPDF: { format: size, orientation: "portrait" },
    };
    html2pdf().set(opt).from(printElement).save();
  }, []);

  /** Handle get height for table */
  // const gridWrapperRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    // const gridDiv = gridWrapperRef.current;
    // if (gridDiv) {
    // const gridEl: HTMLDivElement = gridDiv.querySelector("div")!;
    // gridEl.style.height = "";
    // }
  });
  //#endregion event handlers

  return (
    <Box
      id={`${type}-pdf-print`}
      className={`workout-pdf-container ${size}${className ? ` ${className}` : ""}`}
      ref={pdfPrintRef}
    >
      <Box className="logo">
        <IconLogo width={"180px"} />
        <Stack>
          <p className="name">Workout Plan Details {title}</p>
          <p className="date">{moment().format(DATE_FORMAT)}</p>
        </Stack>
      </Box>
      <Stack className="container">
        <PrimaryDetails workoutPlan={workoutPlan} workoutPlanGoals={workoutPlanGoals} />
        <div className="page-break" style={{ pageBreakBefore: "always" }} />
        {workoutPlan?.isCardioProgramIncluded && (
          <CardioProgramSection
            workoutPlan={workoutPlan}
            cardioProgram={cardioProgram}
            perceivedExertion={perceivedExertion}
          />
        )}
        <div className="html2pdf__page-break"></div>
        {workoutPlan?.isStrengthProgramIncluded && (
          <StrengthProgramSection strengthProgram={strengthProgram} />
        )}
        <div className="page-break" style={{ pageBreakBefore: "always" }} />
        {workoutPlan?.isFlexibilityProgramIncluded && (
          <FlexibilityProgramSection
            workoutPlan={workoutPlan}
            flexibilityUpperBodyStretchesOptionsFromParent={
              flexibilityUpperBodyStretchesOptionsFromParent
            }
            flexibilityLowerBodyStretchesOptionsFromParent={
              flexibilityLowerBodyStretchesOptionsFromParent
            }
          />
        )}
      </Stack>
    </Box>
  );
});

const PrimaryDetails = (props: { workoutPlan: WorkoutPlan; workoutPlanGoals: string }) => {
  const { workoutPlan, workoutPlanGoals } = props;

  return (
    <Stack className="section" gap={1}>
      <p className="title">Primary Details</p>
      <Stack direction="row" gap={1}>
        <Box className="primary-details-item">
          <label>Workout Plan Name</label>
          <p>{workoutPlan?.workout_plan_name}</p>
        </Box>
        <Box className="primary-details-item">
          <label>Included Programs</label>
          <p className="programsIncluded">
            {workoutPlan?.isFlexibilityProgramIncluded && (
              <>
                <IconFlexibility />
                <span>Flexibility</span>
              </>
            )}
            {workoutPlan?.isCardioProgramIncluded && (
              <>
                <IconCardio />
                <span>Cardiovascular</span>
              </>
            )}
            {workoutPlan?.isStrengthProgramIncluded && (
              <>
                <IconStrength />
                <span>Strength</span>
              </>
            )}
          </p>
        </Box>
      </Stack>

      <Stack direction="row" gap={1}>
        <Box className="primary-details-item">
          <label>Goals</label>
          <p>{workoutPlanGoals || `N/A`}</p>
        </Box>
        <Box className="primary-details-item">
          <label>Additional Goals</label>
          <p>
            {workoutPlan?.additional_goals.length > 0
              ? workoutPlan?.additional_goals.join(", ")
              : `N/A`}
          </p>
        </Box>
      </Stack>

      <Stack direction="row" gap={1}>
        <Box className="primary-details-item">
          <label>Instructor Notes</label>
          <p>{workoutPlan?.instructor_notes || `N/A`}</p>
        </Box>
        <Box className="primary-details-item">
          <label>Warm Up Notes</label>
          <p>{workoutPlan?.warm_up_notes || `N/A`}</p>
        </Box>
      </Stack>
    </Stack>
  );
};

const FlexibilityProgramSection = (props: {
  workoutPlan: WorkoutPlan;
  flexibilityUpperBodyStretchesOptionsFromParent?: any[];
  flexibilityLowerBodyStretchesOptionsFromParent?: any[];
}) => {
  const {
    workoutPlan,
    flexibilityUpperBodyStretchesOptionsFromParent,
    flexibilityLowerBodyStretchesOptionsFromParent,
  } = props;

  const upperBodyExercises =
    flexibilityUpperBodyStretchesOptionsFromParent?.filter((f) =>
      workoutPlan?.stretch_exercises.includes(f.id)
    ) || [];
  const lowerBodyExercises =
    flexibilityLowerBodyStretchesOptionsFromParent?.filter((f) =>
      workoutPlan?.stretch_exercises.includes(f.id)
    ) || [];

  return (
    <Stack className="section" gap={1} mt={3}>
      <p className="title">Flexibility Program</p>
      {upperBodyExercises.length > 0 && (
        <>
          <Box className="primary-details-item">
            <label>Upper Body Stretches</label>
          </Box>
          <Grid container spacing={3}>
            {upperBodyExercises.map((m, i) => (
              <Grid item xs={6}>
                <Box className="stretchTitle">{m.title}</Box>
                <Box className="stretchInstructions">
                  <img alt="m.title" src={m.image} />
                </Box>
                <Box>{m.instructions}</Box>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      {lowerBodyExercises.length > 0 && (
        <>
          <Box className="primary-details-item">
            <label>Lower Body Stretches</label>
          </Box>
          <Grid container spacing={3}>
            {lowerBodyExercises.map((m, i) => (
              <Grid item xs={6}>
                <Box className="stretchTitle">{m.title}</Box>
                <Box className="stretchInstructions">
                  <img alt="m.title" src={m.image} />
                </Box>
                <Box className="stretchInstructions">{m.instructions}</Box>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Stack>
  );
};

const CardioProgramSection = (props: {
  workoutPlan: WorkoutPlan;
  cardioProgram: CardioProgram;
  perceivedExertion: string;
}) => {
  const { workoutPlan, cardioProgram, perceivedExertion } = props;
  const cardioGoal =
    workoutPlan?.cardio_overall_goal?.goal === "TIME"
      ? `Time`
      : workoutPlan?.cardio_overall_goal?.goal === "CALORIES"
      ? `Calories`
      : `Heart Rate`;
  const activities = cardioProgram.exercises.filter((f) => f.type === "ACTIVITIES");
  const machinesConnectedToFitLinxx = cardioProgram.exercises.filter(
    (f) => f.type === "MACHINES_CONNECTED_TO_FITLINXX"
  );
  const machinesNotConnectedToFitLinxx = cardioProgram.exercises.filter(
    (f) => f.type === "MACHINES_NOT_CONNECTED_TO_FITLINXX"
  );
  const classes = cardioProgram.exercises.filter((f) => f.type === "CLASSES");

  return (
    <Stack className="section" gap={1} mt={3}>
      <p className="title">Cardio Program</p>

      <p className="subsection-title">Overal Goals</p>
      <Stack direction="row" gap={1}>
        <Box className="primary-details-item">
          <p>{workoutPlan?.cardio_program_frequency}</p>
          <label>Sessions per week</label>
        </Box>
        <Box className="primary-details-item">
          <p>{`${workoutPlan?.heart_rate_min}-${workoutPlan?.heart_rate_max}`}</p>
          <label>Heart Rate, BPM</label>
        </Box>
        <Box className="primary-details-item">
          <p>{cardioGoal}</p>
          <label>Goal</label>
        </Box>
        <Box className="primary-details-item">
          <p>{`${workoutPlan?.cardio_overall_goal?.daily_min_value}-${workoutPlan?.cardio_overall_goal?.daily_max_value}`}</p>
          <label>Minutes per Session</label>
        </Box>
        <Box className="primary-details-item">
          <p>{`${workoutPlan?.cardio_overall_goal?.weekly_min_value}-${workoutPlan?.cardio_overall_goal?.weekly_max_value}`}</p>
          <label>Minutes Weekly</label>
        </Box>
        <Box className="primary-details-item">
          <p>{perceivedExertion}</p>
          <label>Perceived Exeption</label>
        </Box>
      </Stack>

      <p className="subsection-title">
        <span>{cardioProgram.name}</span>
        {cardioProgram.is_active && <span>active</span>}{" "}
        {cardioProgram.is_express && <span>express</span>}
      </p>

      <TableContainer className="cardio-table">
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell component="th"></TableCell>
              <TableCell component="th" className="" align="left">
                Exercise
              </TableCell>
              <TableCell component="th" sx={{ textAlign: "center" }}>
                Time
              </TableCell>
            </TableRow>
          </TableHead>
          {activities.length > 0 && (
            <TableBody className="rowGroup">
              {activities.map((m, i) => (
                <TableRow key={`cardioProgramActivities${i}`}>
                  {i === 0 && (
                    <TableCell className="title" rowSpan={activities.length}>
                      Activities
                    </TableCell>
                  )}
                  <TableCell>{m.name}</TableCell>
                  <TableCell align="center">{m.time}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
          {machinesConnectedToFitLinxx.length > 0 && (
            <TableBody className="rowGroup">
              {machinesConnectedToFitLinxx.map((m, i) => (
                <TableRow key={`cardioProgramMachinesConnectedToFitLinxx${i}`}>
                  {i === 0 && (
                    <TableCell className="title" rowSpan={machinesConnectedToFitLinxx.length}>
                      Machines Connected to FitLinxx
                    </TableCell>
                  )}
                  <TableCell>{m.name}</TableCell>
                  <TableCell align="center">{m.time}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
          {machinesNotConnectedToFitLinxx.length > 0 && (
            <TableBody className="rowGroup">
              {machinesNotConnectedToFitLinxx.map((m, i) => (
                <TableRow key={`cardioProgramMachinesNotConnectedToFitLinxx${i}`}>
                  {i === 0 && (
                    <TableCell className="title" rowSpan={machinesNotConnectedToFitLinxx.length}>
                      Machines Not Connected to FitLinxx
                    </TableCell>
                  )}
                  <TableCell>{m.name}</TableCell>
                  <TableCell align="center">{m.time}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
          {classes.length > 0 && (
            <TableBody className="rowGroup">
              {classes.map((m, i) => (
                <TableRow key={`cardioProgramClasses${i}`}>
                  {i === 0 && (
                    <TableCell className="title" rowSpan={classes.length}>
                      Classes
                    </TableCell>
                  )}
                  <TableCell>{m.name}</TableCell>
                  <TableCell align="center">{m.time}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Stack>
  );
};

const StrengthProgramSection = (props: { strengthProgram: StrengthProgram }) => {
  const { strengthProgram } = props;
  const [maximumNumberOfSets, setMaximumNumberOfSets] = useState(0);

  useEffect(() => {
    if (strengthProgram) {
      let maxNbSets = 0;
      strengthProgram.exercises.forEach((e) => {
        if (e.sets.length > maxNbSets) maxNbSets = e.sets.length;
      });
      setMaximumNumberOfSets(maxNbSets);
    }
  }, [strengthProgram]);

  return (
    <Stack className="section" gap={1} mt={3}>
      <p className="title">Strength Program</p>

      <p className="subsection-title">
        <span>{strengthProgram.name}</span>
        {strengthProgram.is_active && <span>active</span>}{" "}
        {strengthProgram.is_express && <span>express</span>}
      </p>

      <TableContainer className="strength-table">
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell component="th" colSpan={maximumNumberOfSets + 2}>
                Targets <br /> <span style={{ fontWeight: 200 }}>Workouts per week</span>{" "}
                {strengthProgram.per_week}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Exercises</TableCell>
              <TableCell align="left">Machine Settings</TableCell>
              {Array(maximumNumberOfSets)
                .fill(0)
                .map((_m, i) => (
                  <TableCell key={`setCounter${i}`}>
                    <Stack gap={1}>
                      <p>Set {i + 1}</p>
                      <Stack direction="row" fontSize={10} fontWeight={200} gap={1}>
                        <Box flex={1}>W</Box>
                        <Box flex={1}>Min</Box>
                        <Box flex={1}>Max</Box>
                        <Box flex={1}>Rest</Box>
                      </Stack>
                    </Stack>
                  </TableCell>
                ))}
            </TableRow>
            {strengthProgram.exercises?.map((m, i) => (
              <TableRow key={`exerciseSetRow${i}`}>
                <TableCell className="bold" align="left">
                  {m.name}
                </TableCell>
                <TableCell align="left">{m.settings}</TableCell>
                {m.sets?.map((set, setIndex) => (
                  <TableCell align="left" key={`exerciseSetCol${i}_${setIndex}`}>
                    <Stack direction="row" gap={1}>
                      <Box flex={1}>{set.weight}</Box>
                      <Box flex={1}>{set.reps_min}</Box>
                      <Box flex={1}>{set.reps_max}</Box>
                      <Box flex={1}>{set.rest_time}</Box>
                    </Stack>
                  </TableCell>
                ))}
                {m.sets.length < maximumNumberOfSets &&
                  Array(maximumNumberOfSets - m.sets.length)
                    .fill(0)
                    .map((_m, index) => (
                      <TableCell align="left" key={`exerciseSetCol${i}_${index}`}>
                        &nbsp;
                      </TableCell>
                    ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

const GeneratePdf = memo(GeneratePdfComp);
export default GeneratePdf;
